// Settings — alert thresholds (client-side risk recompute), display density, timestamp
// timezone mode, live-feed pause/resume, and the browser↔server connection status. No demo
// or trade controls exist anywhere — this is a read-only console.

import { C, MONO } from '../lib/tokens';
import { fmtAge, fmtTS } from '../lib/format';
import type { Thresholds } from '../lib/mappers';
import { CHIP_BASE, CHIP_ON, GBTN } from '../lib/styles';
import { useData, useServerNow, useUi } from '../store/hooks';
import { setThreshold, uiStore } from '../store/uiStore';
import { feed } from '../store/dataStore';
import { MfaCard } from '../components/MfaCard';
import { AgentsCard } from '../components/AgentsCard';
import { InvitesCard } from '../components/InvitesCard';

const THRESHOLD_ROWS: Array<[string, keyof Thresholds]> = [
  ['Margin level warning below', 'mW'],
  ['Margin level critical below', 'mC'],
  ['Drawdown warning above', 'dW'],
  ['Drawdown critical above', 'dC'],
  ['Daily loss warning above', 'lW'],
];

export function Settings() {
  const thr = useUi((s) => s.thr);
  const dens = useUi((s) => s.dens);
  const tz = useUi((s) => s.tz);
  const feedStatus = useData((s) => s.feedStatus);
  const lastEventAt = useData((s) => s.lastEventAt);
  const revision = useData((s) => s.revision);
  const clockOffset = useData((s) => s.clockOffsetMs);
  const paused = useData((s) => s.paused);
  const serverNow = useServerNow();

  const feedColor = feedStatus === 'live' ? C.gn : feedStatus === 'offline' ? C.rd : C.am;

  return (
    <section data-screen-label="Settings" style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 880 }}>
      <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Settings</h1>

      <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '12px 14px' }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 600 }}>Alert thresholds</h2>
        {THRESHOLD_ROWS.map(([label, key]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: '1px solid #16203A' }}>
            <span style={{ flex: 1, fontSize: 11.5, color: C.b9 }}>{label}</span>
            <input
              type="number"
              value={thr[key]}
              onChange={(e) => setThreshold(key, parseFloat(e.target.value))}
              style={{ width: 72, height: 26, background: '#0A0F19', border: '1px solid ' + C.ln, borderRadius: 5, color: C.tx, fontSize: 11.5, padding: '0 8px', fontFamily: MONO, textAlign: 'right' }}
            />
            <span style={{ fontSize: 11, color: C.dm, width: 14 }}>%</span>
          </div>
        ))}
        <div style={{ fontSize: 10.5, color: C.dim2, marginTop: 7 }}>Risk statuses, quick filters and coloring recompute immediately across the dashboard.</div>
      </div>

      <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>Display</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11.5, color: C.dm, width: 110 }}>Table density</span>
          {(['compact', 'comfortable'] as const).map((v) => (
            <button key={v} onClick={() => uiStore.set({ dens: v })} aria-pressed={dens === v} style={{ ...(dens === v ? CHIP_ON : CHIP_BASE), padding: '3px 9px', fontSize: 10.5 }}>
              {v === 'compact' ? 'Compact' : 'Comfortable'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11.5, color: C.dm, width: 110 }}>Timestamps</span>
          {([['both', 'Local + broker'], ['local', 'Local only'], ['broker', 'Broker time']] as const).map(([v, label]) => (
            <button key={v} onClick={() => uiStore.set({ tz: v })} aria-pressed={tz === v} style={{ ...(tz === v ? CHIP_ON : CHIP_BASE), padding: '3px 9px', fontSize: 10.5 }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <MfaCard />

      <InvitesCard />

      <AgentsCard />

      <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>Live feed</h2>
        <div style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.5 }}>
          Updates arrive over a revisioned SSE stream; changed cells flash briefly and rows never re-render wholesale. On disconnect the last
          snapshot is preserved and marked stale; missing values show as “Unavailable”, never zero. Reconnection resynchronizes automatically.
        </div>
        <div>
          <button onClick={() => feed.setPaused(!paused)} style={GBTN}>
            {paused ? 'Resume live feed' : 'Pause live feed'}
          </button>
        </div>
      </div>

      <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '12px 14px' }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 600 }}>Connection status</h2>
        {(
          [
            ['Browser ↔ server', feedStatus, feedColor],
            ['Last update', lastEventAt ? fmtAge(serverNow - lastEventAt) + ' ago' : 'never', undefined],
            ['Stream revision', String(revision), undefined],
            ['Clock offset (server − local)', (clockOffset >= 0 ? '+' : '') + Math.round(clockOffset) + ' ms', undefined],
            ['Server time', fmtTS(serverNow), undefined],
          ] as Array<[string, string, string | undefined]>
        ).map(([k, v, color]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0', borderBottom: '1px solid #16203A' }}>
            <span style={{ fontSize: 11.5, color: C.dm }}>{k}</span>
            <span style={{ fontSize: 11.5, fontFamily: MONO, color: color ?? C.tx }}>{v}</span>
          </div>
        ))}
        <div style={{ fontSize: 10.5, color: C.dim2, marginTop: 7 }}>
          The browser connection indicator is independent of terminal/collector health — a browser disconnect never means the fleet is offline.
        </div>
      </div>
    </section>
  );
}
